import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material';
import { Link } from '@gapartners/ui';
import { getClientSpaceWorkspaceUrl } from '@/utils/clientSpace';

export interface ClientSpaceWorkspaceLinkProps {
  /** ClientSpace workspace id. Link is not rendered when missing/invalid. */
  workspaceId: string | number | null | undefined;
  /** Custom link content (e.g. the client name). Defaults to "Open workspace". */
  children?: ReactNode;
  sx?: SxProps<Theme>;
}

/**
 * Opens the matching ClientSpace workspace in a new tab. Renders nothing when
 * no workspace id is available, matching the acceptance criteria.
 */
export function ClientSpaceWorkspaceLink({
  workspaceId,
  children,
  sx,
}: ClientSpaceWorkspaceLinkProps) {
  const { t } = useTranslation();
  const url = getClientSpaceWorkspaceUrl(workspaceId);
  if (!url) return null;

  return (
    <Link
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      sx={{
        cursor: 'pointer',
        textTransform: 'none',
        fontWeight: 600,
        ...sx,
      }}
    >
      {children ?? t('components.contactCenter.openWorkspace')}
    </Link>
  );
}
